import React, { useState, useMemo } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  TrendingUp, 
  Users, 
  Globe, 
  Target,
  MousePointer,
  BarChart3,
  Calendar,
  ExternalLink,
  UserCheck,
  Activity
} from 'lucide-react';
import { Authenticated } from '../../lib/auth/auth-components';
import { Id } from '../../../convex/_generated/dataModel';
import { useToast } from '../ui/use-toast';

interface VisitorAnalytics {
  totalVisits: number;
  uniqueVisitors: number;
  totalPageViews: number;
  conversionRate: number;
  bySource: Record<string, number>;
  byCampaign: Record<string, number>;
  byMedium: Record<string, number>;
  topReferrers: Record<string, number>;
  topLandingPages: Record<string, number>;
  returningVisitorCount: number;
  newVisitorCount: number;
}

interface VisitorSession {
  _id: Id<"visitorSessions">;
  visitorId: string;
  sessionId: string;
  utmId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPage: string;
  referrer?: string;
  createdAt: number;
  pageViews: number;
  lastActivityAt: number;
  leadSubmitted?: boolean;
  leadId?: Id<"leads">;
}

const VisitorAnalyticsContent: React.FC = () => {
  const { useAuthenticatedQuery, sessionToken, isAuthenticated } = useAuth();
  const [selectedProject, setSelectedProject] = useState<Id<"projects"> | null>(null);
  const [selectedVisitorId, setSelectedVisitorId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const { toast } = useToast();

  // Get projects
  const projects = useAuthenticatedQuery('projects:list', {}) || [];

  // Memoize date range calculation to prevent infinite re-renders
  const dateRangeValues = useMemo(() => {
    const now = Date.now();
    const startDate = (() => {
      switch (dateRange) {
        case '7d': return now - 7 * 24 * 60 * 60 * 1000;
        case '30d': return now - 30 * 24 * 60 * 60 * 1000;
        case '90d': return now - 90 * 24 * 60 * 60 * 1000;
        default: return undefined;
      }
    })();
    
    return {
      startDate,
      endDate: now
    };
  }, [dateRange]);

  // Get analytics data
  const analytics = useAuthenticatedQuery(
    'visitorTracking:getVisitorAnalytics',
    selectedProject ? {
      projectId: selectedProject,
      startDate: dateRangeValues.startDate,
      endDate: dateRangeValues.endDate
    } : "skip"
  ) as VisitorAnalytics | undefined;

  // Debug logging with reduced frequency
  console.log('Analytics query state:', {
    selectedProject,
    analytics: analytics ? 'loaded' : 'undefined',
    dateRange,
    hasStartDate: !!dateRangeValues.startDate,
    sessionToken: sessionToken ? 'present' : 'missing',
    isAuthenticated,
  });

  // Get visitor details if selected
  const visitorDetails = useAuthenticatedQuery(
    'visitorTracking:getVisitorDetails',
    selectedVisitorId ? { visitorId: selectedVisitorId } : "skip"
  );

  // Get recent visitor sessions for journey view
  const recentSessions = useAuthenticatedQuery(
    'visitorTracking:getRecentSessions',
    selectedProject ? { projectId: selectedProject, limit: 20 } : "skip"
  );

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getSourceIcon = (source: string) => {
    switch (source.toLowerCase()) {
      case 'google': return '🔍';
      case 'facebook': return '📘';
      case 'instagram': return '📷';
      case 'linkedin': return '💼';
      case 'direct': return '🔗';
      default: return '🌐';
    }
  };

  const getMediumBadgeColor = (medium: string) => {
    switch (medium.toLowerCase()) {
      case 'cpc': return 'bg-blue-100 text-blue-800';
      case 'organic': return 'bg-green-100 text-green-800';
      case 'social': return 'bg-purple-100 text-purple-800';
      case 'email': return 'bg-yellow-100 text-yellow-800';
      case 'direct': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedProject) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Visitor Analytics</h1>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">Please select a project to view analytics</p>
              <div className="mt-4">
                <Select value={selectedProject || ""} onValueChange={(value) => setSelectedProject(value as Id<"projects">)}>
                  <SelectTrigger className="w-[300px] mx-auto">
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project._id} value={project._id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Visitor Analytics</h1>
        <div className="flex items-center gap-4">
          <Select value={dateRange} onValueChange={(value: any) => setDateRange(value)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedProject || ""} onValueChange={(value) => setSelectedProject(value as Id<"projects">)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project._id} value={project._id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {analytics ? (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.totalVisits.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.totalPageViews.toLocaleString()} page views
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.uniqueVisitors.toLocaleString()}</div>
                <div className="flex items-center gap-4 mt-1">
                  <p className="text-xs text-muted-foreground">
                    New: {analytics.newVisitorCount}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Returning: {analytics.returningVisitorCount}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                <UserCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatPercentage(analytics.conversionRate)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Visitors who submitted a lead
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg. Pages/Visit</CardTitle>
                <MousePointer className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analytics.totalVisits > 0 
                    ? (analytics.totalPageViews / analytics.totalVisits).toFixed(1)
                    : '0'
                  }
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Pages viewed per visit
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Analytics Tabs */}
          <Tabs defaultValue="sources" className="space-y-4">
            <TabsList>
              <TabsTrigger value="sources">Traffic Sources</TabsTrigger>
              <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
              <TabsTrigger value="visitors">Visitor Journey</TabsTrigger>
            </TabsList>

            <TabsContent value="sources" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Traffic Sources
                    </CardTitle>
                    <CardDescription>Where your visitors come from</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.bySource)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([source, count]) => (
                          <div key={source} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getSourceIcon(source)}</span>
                              <span className="font-medium capitalize">{source}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{count}</span>
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-primary h-2 rounded-full"
                                  style={{ width: `${(count / analytics.totalVisits) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Mediums */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Traffic Mediums
                    </CardTitle>
                    <CardDescription>How visitors reach your site</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.byMedium)
                        .sort(([, a], [, b]) => b - a)
                        .map(([medium, count]) => (
                          <div key={medium} className="flex items-center justify-between">
                            <Badge className={getMediumBadgeColor(medium)}>
                              {medium || 'none'}
                            </Badge>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{count}</span>
                              <span className="text-xs text-muted-foreground">
                                ({formatPercentage((count / analytics.totalVisits) * 100)})
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Referrers */}
              {Object.keys(analytics.topReferrers).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ExternalLink className="w-5 h-5" />
                      Top Referrers
                    </CardTitle>
                    <CardDescription>External sites sending traffic</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(analytics.topReferrers)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 5)
                        .map(([referrer, count]) => (
                          <div key={referrer} className="flex items-center justify-between text-sm">
                            <span className="truncate max-w-md">{referrer}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="campaigns" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Campaign Performance
                  </CardTitle>
                  <CardDescription>Track your marketing campaigns</CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.keys(analytics.byCampaign).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Campaign</th>
                            <th className="text-right py-2">Visits</th>
                            <th className="text-right py-2">% of Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(analytics.byCampaign)
                            .sort(([, a], [, b]) => b - a)
                            .map(([campaign, count]) => (
                              <tr key={campaign} className="border-b">
                                <td className="py-2">
                                  <span className="font-medium">{campaign}</span>
                                </td>
                                <td className="text-right py-2">{count}</td>
                                <td className="text-right py-2">
                                  {formatPercentage((count / analytics.totalVisits) * 100)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No campaign data available</p>
                      <p className="text-sm mt-2">Campaign data will appear when visitors arrive with utm_campaign parameters</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="visitors" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Visitor Journey Tracking</CardTitle>
                  <CardDescription>
                    Track individual visitors across multiple sessions and campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {recentSessions && recentSessions.length > 0 ? (
                    <div className="space-y-4">
                      {recentSessions.map((session: any) => (
                        <div 
                          key={session._id} 
                          className={`border rounded-lg p-4 transition-colors ${
                            session.leadSubmitted 
                              ? 'border-green-200 bg-green-50' 
                              : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${
                                session.leadSubmitted ? 'bg-green-500' : 'bg-blue-500'
                              }`} />
                              <div>
                                <div className="font-medium text-sm">
                                  Visitor: {session.visitorId.slice(-8)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {formatDate(session.createdAt)}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {session.pageViews} pages
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {session.leadSubmitted ? 'Converted' : 'Browsing'}
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">SOURCE</label>
                              <div className="flex items-center gap-2 mt-1">
                                <span>{getSourceIcon(session.utmSource || 'direct')}</span>
                                <span className="capitalize">{session.utmSource || 'direct'}</span>
                              </div>
                            </div>

                            <div>
                              <label className="text-xs font-medium text-muted-foreground">CAMPAIGN</label>
                              <div className="mt-1">
                                {session.utmCampaign ? (
                                  <Badge variant="outline" className="text-xs">
                                    {session.utmCampaign}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">None</span>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="text-xs font-medium text-muted-foreground">LANDING PAGE</label>
                              <div className="mt-1 text-xs text-muted-foreground truncate">
                                {session.landingPage}
                              </div>
                            </div>
                          </div>

                          {session.leadData && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-green-700">
                                    Lead Submitted: {session.leadData.name}
                                  </div>
                                  <div className="text-xs text-green-600">
                                    {session.leadData.email} • Status: {session.leadData.status}
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600 border-green-200 hover:bg-green-100"
                                  onClick={() => setSelectedVisitorId(session.visitorId)}
                                >
                                  View Full Journey
                                </Button>
                              </div>
                            </div>
                          )}

                          {!session.leadSubmitted && (
                            <div className="mt-3 pt-3 border-t">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:bg-blue-50"
                                onClick={() => setSelectedVisitorId(session.visitorId)}
                              >
                                View Visitor Journey
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No visitor sessions found</p>
                      <p className="text-sm mt-2">Visitor sessions will appear here as people visit your site</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card>
          <CardContent className="pt-6 text-center">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Loading analytics data...</p>
          </CardContent>
        </Card>
      )}

      {/* Visitor Journey Modal */}
      {selectedVisitorId && visitorDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Visitor Journey</h2>
                <p className="text-sm text-muted-foreground">
                  Visitor ID: {selectedVisitorId}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedVisitorId(null)}
              >
                ✕
              </Button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Visitor Profile Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{visitorDetails.profile.totalVisits}</div>
                    <div className="text-xs text-muted-foreground">Total Visits</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{visitorDetails.profile.totalPageViews}</div>
                    <div className="text-xs text-muted-foreground">Page Views</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{visitorDetails.profile.leadsSubmitted}</div>
                    <div className="text-xs text-muted-foreground">Leads Submitted</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{visitorDetails.profile.trafficSources}</div>
                    <div className="text-xs text-muted-foreground">Traffic Sources</div>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Session Timeline</h3>
                {visitorDetails.sessions.map((session: any, index: number) => (
                  <div key={session._id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-4 h-4 rounded-full ${
                        session.leadSubmitted ? 'bg-green-500' : 'bg-blue-500'
                      }`} />
                      {index < visitorDetails.sessions.length - 1 && (
                        <div className="w-0.5 h-16 bg-gray-300 mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-medium text-sm">
                              Session {visitorDetails.sessions.length - index}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatDate(session.createdAt)}
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <div>{session.pageViews} pages</div>
                            <div className="text-xs text-muted-foreground">
                              {Math.round((session.lastActivityAt - session.createdAt) / (1000 * 60))} min
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Source</label>
                            <div className="flex items-center gap-2">
                              <span>{getSourceIcon(session.utmSource || 'direct')}</span>
                              <span>{session.utmSource || 'direct'}</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Landing Page</label>
                            <div className="text-xs text-muted-foreground truncate">
                              {session.landingPage}
                            </div>
                          </div>
                        </div>

                        {session.utmCampaign && (
                          <div className="mb-3">
                            <Badge variant="outline" className="text-xs">
                              Campaign: {session.utmCampaign}
                            </Badge>
                          </div>
                        )}

                        {session.leadSubmitted && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="text-sm font-medium text-green-700">
                              ✅ Lead Converted
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Lead Details */}
              {visitorDetails.leads.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">Lead Submissions</h3>
                  <div className="space-y-3">
                    {visitorDetails.leads.map((lead: any) => (
                      <div key={lead._id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium">{lead.name}</div>
                            <div className="text-sm text-muted-foreground">{lead.email}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {formatDate(lead.createdAt)} • Status: {lead.status}
                            </div>
                          </div>
                          <Badge variant="outline" className="text-green-700 border-green-300">
                            {lead.formType || 'form'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const VisitorAnalytics: React.FC = () => {
  return (
    <Authenticated>
      <VisitorAnalyticsContent />
    </Authenticated>
  );
}; 